// A bejelentkezett felhasználó adatai a Firestore-ból.
//
// Az adatmodell a Flutter oldallal közös (lásd docs/01-funkcionalis-specifikacio.md):
//   users/{uid}                  - globális felhasználó, projects[] = cégtagságok
//   projects/{companyId}         - CÉG (a gyökér "projects" kollekció a céget jelöli)
//
// A névhasználat itt szándékosan tisztább, mint a Firestore-ban: amit a
// felhasználó cégnek lát, az a `projects/{id}` dokumentum.

import { doc, getDoc } from 'firebase/firestore';
import type { User } from 'firebase/auth';
import { db } from './firebase';

export type Membership = {
  companyId: string;
  role: string;
};

export type SessionUser = {
  uid: string;
  email: string;
  fullName: string;
  jobTitle?: string;
  photoUrl?: string;
  memberships: Membership[];
  /// Melyik szintű helyet kapta a cégadmintól.
  ///
  /// A cég a Stripe-ban a DARABSZÁMOT veszi meg (hány sima és hány AI-s
  /// hely), a kiosztás pedig cégen belüli ügy. Ez a mező mondja meg, hogy
  /// EZ a felhasználó melyiket kapta — ebből dől el, látja-e az AI-t.
  seatTier?: 'basic' | 'ai';
  companyName?: string;
  /// Az automatikus határidő-emlékeztető beállításai.
  ///
  /// A mező neve és alakja a Flutter oldalról jön (`AutoUserPushModel`):
  /// `users/{uid}.autoUserPush = { day, hours, minutes, pushAfterDeadline }`.
  /// NEM találunk ki új szerződést — a mobil ugyanezt olvassa.
  autoUserPush?: AutoUserPush;
};

/// Határidő-emlékeztető a felhasználó dokumentumán.
export type AutoUserPush = {
  day?: number;
  hours?: number;
  minutes?: number;
  pushAfterDeadline?: boolean;
};

export type SessionRole = 'super' | 'admin' | 'user';

export function roleOf(memberships: Membership[]): SessionRole {
  if (memberships.some((m) => m.role === 'SUPER')) return 'super';
  if (memberships.some((m) => m.role === 'ADMIN')) return 'admin';
  return 'user';
}

/// A felhasználói dokumentum betöltése.
///
/// Ha nincs jogosultság vagy nincs dokumentum, nem dobunk hibát: a belépés
/// attól még érvényes, csak kevesebbet tudunk a felhasználóról. Ilyenkor az
/// e-mail címe a neve — jobb, mint egy üres fejléc vagy egy hibaoldal.
export async function loadSessionUser(user: User): Promise<SessionUser> {
  const fallback: SessionUser = {
    uid: user.uid,
    email: user.email ?? '',
    fullName: user.displayName ?? user.email ?? 'Ismeretlen felhasználó',
    memberships: [],
  };

  try {
    const snapshot = await getDoc(doc(db, 'users', user.uid));
    if (!snapshot.exists()) return fallback;

    const data = snapshot.data() as {
      firstName?: string;
      lastName?: string;
      email?: string;
      jobTitle?: string;
      photoUrl?: string;
      projects?: { id?: string; role?: string }[];
      autoUserPush?: AutoUserPush;
      seatTier?: string;
    };

    // Magyar névsorrend: vezetéknév, majd keresztnév.
    const fullName = [data.lastName, data.firstName].filter(Boolean).join(' ').trim();

    const memberships: Membership[] = (data.projects ?? [])
      .filter((p): p is { id: string; role: string } =>
        typeof p.id === 'string' && typeof p.role === 'string')
      .map((p) => ({ companyId: p.id, role: p.role }));

    return {
      uid: user.uid,
      email: data.email ?? user.email ?? '',
      fullName: fullName.length > 0 ? fullName : fallback.fullName,
      jobTitle: data.jobTitle,
      photoUrl: data.photoUrl,
      memberships,
      autoUserPush: data.autoUserPush,
      // Ismeretlen erteket NEM ertelmezunk AI-kent: egy elgepelt vagy regi
      // mezoertek nem nyithat meg fizetos funkciot.
      seatTier: data.seatTier === 'ai' ? 'ai' : data.seatTier === 'basic' ? 'basic' : undefined,
    };
  } catch (error) {
    console.warn('[BlazeData] A felhasználói adatok betöltése nem sikerült.', error);
    return fallback;
  }
}

/// Az előfizetés-blokk beolvasása.
///
/// A blokk HIÁNYOZHAT: a régebbi cégeknek nincs ilyen mezőjük, és a
/// biztonsági szabályok is korlátlanként kezelik őket. Ilyenkor `undefined`-ot
/// adunk, és a felület nem mutat előfizetés-kártyát — kitalált állapotot
/// viszont nem írunk ki.
export function subscriptionFrom(raw: unknown): SubscriptionView | undefined {
  if (raw === null || typeof raw !== 'object') return undefined;
  const data = raw as Record<string, unknown>;

  const status = data['status'];
  if (
    status !== 'trial' &&
    status !== 'active' &&
    status !== 'past_due' &&
    status !== 'locked'
  ) {
    return undefined;
  }

  const seats = (data['seats'] ?? {}) as Record<string, unknown>;
  const szam = (value: unknown) => (typeof value === 'number' && value > 0 ? value : 0);

  // A megjelenítendő határidő állapotfüggő: próbaidőnél a próbaidő vége,
  // fizetett időszaknál az időszak vége, türelmi időben a türelmi idő vége.
  const mezo =
    status === 'trial'
      ? data['trialEndsAt']
      : status === 'active'
        ? data['currentPeriodEnd']
        : data['graceEndsAt'];

  const deadline = typeof mezo === 'string' ? new Date(mezo) : undefined;

  return {
    status,
    deadline: deadline && !Number.isNaN(deadline.getTime()) ? deadline : undefined,
    seats: { basic: szam(seats['basic']), ai: szam(seats['ai']) },
  };
}

/// A cég neve a fejléchez. Hiba esetén itt sem állunk meg.
export async function loadCompanyName(companyId: string): Promise<string | undefined> {
  return (await loadCompany(companyId)).name;
}

/// Az előfizetés állapota, ahogy a cég dokumentumán él.
///
/// Az értékeket KIZÁRÓLAG a szerver írja (a `subscriptionClock` óra és a
/// Stripe-webhook) — a kliens csak olvassa. Ez fontos: egy kliensoldalon
/// állítható lejárat nem lenne korlát.
export type SubscriptionView = {
  status: 'trial' | 'active' | 'past_due' | 'locked';
  /// A most érvényes határidő: próbaidőnél a próbaidő vége, fizetett
  /// időszaknál az időszak vége, türelmi időben a türelmi idő vége.
  deadline?: Date;
  seats: { basic: number; ai: number };
};

/// A cégdokumentum adatai: név, férőhelykeret és előfizetés.
///
/// A `maxUser` eddig is ott volt a dokumentumon, de a web nem olvasta —
/// a felhasználólistán (W23) ezért nem látszott, hány hely maradt.
export async function loadCompany(
  companyId: string,
): Promise<{ name?: string; maxUser?: number; subscription?: SubscriptionView }> {
  try {
    const snapshot = await getDoc(doc(db, 'projects', companyId));
    const data = snapshot.data() as
      | { name?: unknown; maxUser?: unknown; subscription?: Record<string, unknown> }
      | undefined;
    return {
      name: typeof data?.name === 'string' ? data.name : undefined,
      maxUser:
        typeof data?.maxUser === 'number' && data.maxUser > 0 ? data.maxUser : undefined,
      subscription: subscriptionFrom(data?.subscription),
    };
  } catch {
    return {};
  }
}
